/* global TYPESENSE_CONFIG */
import { SearchClient as TypesenseSearchClient } from 'typesense'
import { typesenseServerConfig } from './searchClient'

// Autocomplete on Typesense: typesense-js directly, not the InstantSearch
// adapter — two collections in one round trip, infix matching, a different
// sort per collection. Categories are resolved at seed time onto the
// suggestion document; #withCategoryRows rebuilds what Algolia's
// query-suggestions plugin did with them.
// See docs/search/typesense-relevance.md.

// autocomplete-js splits on these sentinels, not <mark>. Getting them wrong
// does not error — the dropdown just renders nothing emboldened.
const HIGHLIGHT_PRE_TAG = '__aa-highlight__'
const HIGHLIGHT_POST_TAG = '__/aa-highlight__'

// Unbucketed, unlike the PLP: bucketing here drops the exact match into the
// same band as longer phrases containing it, and popularity wins.
const SUGGESTION_SORT = '_text_match:desc,popularity:desc'

// Only the first two suggestions with categories expand, two categories
// each, so the dropdown grows by at most four rows.
const ITEMS_WITH_CATEGORIES = 2
const CATEGORIES_PER_ITEM = 2

// The field Algolia's plugin stamped on category rows; kept verbatim so the
// template reads one field name whichever engine served the row.
const CATEGORY_KEY = '__autocomplete_qsCategory'

let memoisedClient = null

function client () {
  if (memoisedClient) return memoisedClient

  memoisedClient = new TypesenseSearchClient({
    apiKey: TYPESENSE_CONFIG.searchApiKey,
    ...typesenseServerConfig(),
    connectionTimeoutSeconds: 5,
    // Typing-and-backspacing re-issues near-identical queries.
    cacheSearchResultsForSeconds: 60,
    // Cross-user cache: prefixes repeat across users and neither dropdown
    // half is personalised. Mirrors searchClient.js#serverCacheEnabled.
    useServerSideSearchCache: TYPESENSE_CONFIG.searchParameters.use_cache === true
  })
  return memoisedClient
}

export function suggestionCollection () {
  return TYPESENSE_CONFIG.suggestionCollection
}

// Reshape a Typesense hit to the Algolia shape consumers expect.
function toAlgoliaHit (hit) {
  const document = { ...hit.document }
  document.objectID = String(document.id)

  const highlight = hit.highlight || {}
  document._highlightResult = Object.keys(highlight).reduce((result, field) => {
    const entry = highlight[field]
    if (!entry || typeof entry !== 'object') return result

    const value = entry.snippet || entry.value
    if (!value) return result

    result[field] = {
      value,
      matchLevel: 'full',
      matchedWords: entry.matched_tokens || []
    }
    return result
  }, {})

  return document
}

// Expands suggestions into dropdown rows: each suggestion, then an indented
// "in <Category>" row per category directly beneath it. `category_names` is
// ranked at seed time, so slicing takes the query's most common categories.
export function withCategoryRows (suggestions, {
  itemsWithCategories = ITEMS_WITH_CATEGORIES,
  categoriesPerItem = CATEGORIES_PER_ITEM
} = {}) {
  let expanded = 0

  return suggestions.reduce((rows, suggestion) => {
    rows.push(suggestion)
    if (expanded >= itemsWithCategories) return rows

    const categories = (suggestion.category_names || []).slice(0, categoriesPerItem)
    // A suggestion with no categories does not use up the budget.
    if (categories.length === 0) return rows

    expanded += 1
    categories.forEach((category) => rows.push({ ...suggestion, [CATEGORY_KEY]: category }))
    return rows
  }, [])
}

// One multi_search for both halves of the dropdown: the listing previews and the
// query suggestions. Two collections, one round trip per keystroke.
export function searchAutocompleteCollections (query, { listingsLimit = 5, suggestionsLimit = 5 } = {}) {
  const { collection, searchParameters } = TYPESENSE_CONFIG

  const searches = [
    {
      collection,
      q: query,
      query_by: searchParameters.query_by,
      query_by_weights: searchParameters.query_by_weights,
      num_typos: searchParameters.num_typos,
      min_len_1typo: searchParameters.min_len_1typo,
      min_len_2typo: searchParameters.min_len_2typo,
      // Only what the dropdown template reads.
      include_fields: 'id,title,slug,path_hierarchy,main_photo',
      highlight_fields: 'title',
      per_page: listingsLimit,
      sort_by: searchParameters.sort_by
    },
    {
      collection: suggestionCollection(),
      q: query,
      query_by: 'query',
      // Without infix, "board" suggests nothing ("sideboard" is a mid-word match).
      infix: 'always',
      highlight_fields: 'query',
      per_page: suggestionsLimit,
      sort_by: SUGGESTION_SORT
    }
  ]

  return client()
    .multiSearch.perform(
      { searches },
      {
        highlight_start_tag: HIGHLIGHT_PRE_TAG,
        highlight_end_tag: HIGHLIGHT_POST_TAG
      }
    )
    .then((response) => {
      const [listings, suggestions] = response.results
      return {
        listings: (listings?.hits || []).map(toAlgoliaHit),
        suggestions: withCategoryRows((suggestions?.hits || []).map(toAlgoliaHit))
      }
    })
}
